import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { SplitLines } from "@/components/ui-custom/Reveal";
import { resolveUrl } from "@/lib/api";
import { EASE } from "@/lib/motion";

const splitTitle = (t) => {
  const words = t.split(" ");
  const n = Math.max(2, Math.min(4, Math.round(t.length / 17)));
  const target = t.length / n;
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    const cand = cur ? `${cur} ${w}` : w;
    if (cur && lines.length < n - 1 && Math.abs(cand.length - target) > Math.abs(cur.length - target)) { lines.push(cur); cur = w; }
    else cur = cand;
  });
  if (cur) lines.push(cur);
  return lines;
};

export const Hero = ({ home }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const rx = useMotionValue(0), ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 60, damping: 20 }), sry = useSpring(ry, { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 6);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 6);
  };

  const lines = splitTitle(home.hero_title);

  return (
    <section ref={ref} onMouseMove={onMove} onMouseLeave={() => { rx.set(0); ry.set(0); }} className="relative min-h-[100svh] pt-[72px] grain overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center min-h-[calc(100svh-72px)] py-12 lg:py-0 relative z-[2]">
        <motion.div style={{ y: textY }} className="lg:col-span-7 lg:pr-6">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.3 }} className="eyebrow text-[var(--terracotta)] mb-8" data-testid="hero-eyebrow">
            {home.hero_eyebrow}
          </motion.p>
          <SplitLines lines={lines} delay={0.45} className="font-display font-normal text-[2.6rem] sm:text-6xl lg:text-[4rem] xl:text-[4.6rem] leading-[0.98] text-[var(--ink)]" data-testid="hero-title" />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 1.1 }} className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[var(--charcoal)] font-light" data-testid="hero-subtitle">
            {home.hero_subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 1.25 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/brands" className="btn-pill btn-terracotta" data-testid="hero-cta-range">View our range <ArrowUpRight size={14} /></Link>
            <Link to="/become-a-stockist" className="btn-pill btn-outline" data-testid="hero-cta-stockist">Become a stockist</Link>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div
            style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
            className="relative w-full max-w-[620px] mx-auto lg:ml-auto"
          >
            <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, ease: EASE, delay: 0.6 }} className="img-frame arch aspect-[4/5] w-full shadow-[0_40px_80px_-30px_rgba(26,27,31,0.35)]">
              <motion.img style={{ y: imgY, scale: 1.15 }} src={resolveUrl(home.hero_image_url)} alt="Gourmet antipasto board" className="absolute inset-0 !transform-gpu" data-testid="hero-image" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.6, duration: 0.8, ease: EASE }} className="absolute -left-3 sm:-left-12 bottom-12 bg-[var(--ink)] text-[var(--cream)] px-5 py-4 rounded-sm shadow-lg" style={{ transform: "translateZ(40px)" }}>
              <p className="font-display text-3xl leading-none">1,000<span className="text-[var(--terracotta)]">+</span></p>
              <p className="eyebrow !text-[0.6rem] text-[var(--stone)] mt-1.5">Stores across Australia</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex items-center gap-2 eyebrow !text-[0.6rem] text-[var(--muted)] z-[2]">
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}><ArrowDown size={12} /></motion.span> Scroll
      </motion.div>
    </section>
  );
};
